import { apiFetch } from './client';
import { useAuth } from '@/lib/auth-store';

export interface RecordingRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  ref_number: string;
  name: string;
  description: string | null;
  folder_id: string | null;
  s3_bucket: string;
  s3_key: string;
  mime_type: string;
  duration_ms: number | null;
  size_bytes: string | null;
  source: 'upload' | 'phone' | 'tts';
  language: 'en' | 'he' | 'yi' | null;
  tags: string[];
  active: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  /** Portal-facing creation mechanism. 'upload' | 'phone' | 'tts' | 'append' | 'prepend' | null. */
  created_via: string | null;
  /** If this recording was produced by POST /:id/append, this is the base. */
  append_base_id: string | null;
  /** If this recording was produced by POST /:id/prepend, this is the base. */
  prepend_base_id: string | null;
  /**
   * Original text script for TTS recordings (migration 0033). NULL for
   * uploads / browser-mic / phone-captured rows. Used by the "Edit text"
   * pencil on TTS recordings — see the regenerateTts endpoint.
   */
  source_text?: string | null;
  /** Polly voice id used at synthesis time. NULL for non-TTS rows. */
  tts_voice?: string | null;
}

/**
 * Source descriptor for the append / prepend endpoints. Matches the
 * backend's discriminated union at /recordings/:id/{append|prepend}.
 */
export type DerivedSource =
  | { kind: 'recording'; recording_id: string }
  | { kind: 'upload'; file_base64: string; mime_type: string }
  | { kind: 'tts'; text: string; language: 'en' | 'he' | 'yi'; voice_id?: string }
  | { kind: 'browser'; file_base64: string; mime_type: string };

export interface DerivedRecordingBody {
  source: DerivedSource;
  name?: string;
  description?: string;
  folder_id?: string | null;
  language?: 'en' | 'he' | 'yi';
}

export type UpdateRecordingBody = Partial<
  Pick<RecordingRow, 'name' | 'description' | 'folder_id' | 'language' | 'tags' | 'active'>
>;

export const recordings = {
  list: (p: {
    folder_id?: string;
    /** When true, only recordings with folder_id IS NULL ("unfiled"). */
    unfiled?: boolean;
    search?: string;
    limit?: number;
    deleted?: boolean;
    customer_id?: string;
  } = {}) => {
    const q = new URLSearchParams();
    if (p.folder_id) q.set('folder_id', p.folder_id);
    if (p.unfiled) q.set('unfiled', '1');
    if (p.search) q.set('search', p.search);
    if (p.limit) q.set('limit', String(p.limit));
    if (p.deleted) q.set('deleted', 'true');
    if (p.customer_id) q.set('customer_id', p.customer_id);
    return apiFetch<{ rows: RecordingRow[] }>(`/api/recordings?${q}`);
  },
  get: (id: string) => apiFetch<RecordingRow>(`/api/recordings/${id}`),
  // apiFetch already skips Content-Type for FormData (see client.ts isFormData check)
  upload: (body: FormData) =>
    apiFetch<RecordingRow>('/api/recordings', { method: 'POST', body }),
  update: (id: string, body: UpdateRecordingBody) =>
    apiFetch<RecordingRow>(`/api/recordings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  /**
   * Soft delete — sends the row to the Recycle Bin.
   * Retained for 90 days unless permanently deleted or auto-purged.
   */
  remove: (id: string) =>
    apiFetch<void>(`/api/recordings/${id}`, { method: 'DELETE' }),
  /**
   * Hard delete — drops the DB row AND removes the underlying file.
   * Gated to super_admin + reseller_owner on the backend.
   */
  removePermanent: (id: string) =>
    apiFetch<void>(`/api/recordings/${id}?permanent=true`, { method: 'DELETE' }),
  /**
   * Restore a soft-deleted recording from the Recycle Bin. Idempotent —
   * restoring a row that's not deleted is a no-op and returns the row
   * as-is.
   */
  restore: (id: string) =>
    apiFetch<RecordingRow>(`/api/recordings/${id}/restore`, { method: 'POST' }),
  /**
   * Streaming URL for an <audio> tag. <audio> can't set Authorization
   * headers, so the backend's audio endpoint accepts the same 15-minute
   * access-token JWT as a `?token=` query parameter. The token is short-
   * lived so this URL goes stale on the next token refresh — consumers
   * should re-read it whenever the auth store's accessToken changes.
   */
  audioUrl: (
    id: string,
    accessToken: string | null | undefined,
    opts: { includeDeleted?: boolean } = {},
  ) => {
    const base = `/api/recordings/${id}/audio`;
    const params = new URLSearchParams();
    if (accessToken) params.set('token', accessToken);
    // Opt-in flag — lets the Recycle Bin player stream soft-deleted
    // recordings. Default (no opt-in) keeps the legacy 404 behaviour
    // for deleted rows that older callers may rely on.
    if (opts.includeDeleted) params.set('include_deleted', '1');
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  },
  /**
   * Ask the backend to originate an outbound call to a verified phone
   * number and run the phone-recording flow. Returns once the originate
   * has been accepted by FreeSWITCH; the actual recording completion is
   * async and surfaces as a new row in the recordings list shortly after.
   */
  callMeToRecord: (body: CallMeToRecordBody) =>
    apiFetch<{ call_id: string }>('/api/recordings/call-me-to-record', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /**
   * Synthesize a new recording from text via Polly and persist it in the
   * recordings library. Returns the new row.
   */
  fromTts: (body: FromTtsBody) =>
    apiFetch<RecordingRow>('/api/recordings/from-tts', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /**
   * Re-synthesize an existing TTS recording with updated text. Overwrites
   * the existing audio file in place; the recording id and all IVR-node
   * references remain valid. Only callable on TTS-sourced recordings;
   * uploads / browser-mic / phone-captured rows return 400 not_tts_recording.
   */
  regenerateTts: (id: string, body: {
    text: string;
    language?: 'en' | 'he' | 'yi';
    voice_id?: string;
  }) =>
    apiFetch<RecordingRow>(`/api/recordings/${id}/regenerate-tts`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /**
   * Preview TTS output without persisting — server streams audio/mpeg bytes.
   * Used by the CreateFromTtsDialog so users can listen before saving.
   *
   * Can't use apiFetch() because it assumes a JSON response body. We still
   * need its auth contract though (Authorization: Bearer + credentials) so we
   * pull the access token off the zustand store directly. If the endpoint
   * 401s we surface a user-visible error rather than silently returning an
   * empty blob that fails to play — that was the "{"error":"unauthenticated"}"
   * behaviour the previous credentials-only fetch produced.
   */
  ttsPreview: async (body: TtsPreviewBody) => {
    const { accessToken } = useAuth.getState();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    const res = await fetch('/api/recordings/tts/preview', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      // Server returns JSON on error ({error: 'tts_not_configured'} etc) —
      // parse and rethrow the code so the dialog can map it to friendly text.
      let errText = '';
      try {
        const errBody = await res.json() as { error?: string; detail?: string };
        errText = errBody.error ?? errBody.detail ?? '';
      } catch { /* body wasn't json */ }
      throw new Error(errText || `tts preview failed: ${res.status}`);
    }
    return res.blob();
  },
  /**
   * Bulk-download recordings as a ZIP archive. Server streams
   * `application/zip`; ids that are missing / soft-deleted / out of tenant
   * are silently skipped server-side. Max 200 ids, 500 MB total.
   *
   * Can't use apiFetch() because it assumes a JSON response body — same
   * reasoning as ttsPreview above. We reuse its auth contract manually
   * (Authorization: Bearer + credentials) and surface the server's JSON
   * error code on failure so callers can toast something friendly.
   */
  downloadZip: async (recordingIds: string[]) => {
    const { accessToken } = useAuth.getState();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    const res = await fetch('/api/recordings/download-zip', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ recording_ids: recordingIds }),
    });
    if (!res.ok) {
      let errText = '';
      try {
        const errBody = await res.json() as { error?: string; detail?: string };
        errText = errBody.error ?? errBody.detail ?? '';
      } catch { /* body wasn't json */ }
      if (errText === 'payload_too_large') {
        throw new Error('Selection exceeds the 500 MB ZIP limit. Deselect some recordings and try again.');
      }
      if (errText === 'no_recordings_found') {
        throw new Error('None of the selected recordings are available to download.');
      }
      throw new Error(errText || `download failed: ${res.status}`);
    }
    return res.blob();
  },
  /**
   * Append new audio onto an existing recording. Always creates a NEW
   * recordings row — the base is left untouched. The server concats via
   * ffmpeg and returns the new row (201). On ffmpeg-missing hosts the
   * server returns 503; callers should surface that as a soft error.
   */
  append: (id: string, body: DerivedRecordingBody) =>
    apiFetch<RecordingRow>(`/api/recordings/${id}/append`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Prepend new audio before an existing recording. Same contract as append. */
  prepend: (id: string, body: DerivedRecordingBody) =>
    apiFetch<RecordingRow>(`/api/recordings/${id}/prepend`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export type CallMeToRecordBody = {
  phone_number: string; // E.164
  target:
    | { mode: 'new'; folder_id?: string | null }
    | { mode: 'append'; recording_id: string }
    | { mode: 'prepend'; recording_id: string }
    | { mode: 'overwrite'; recording_id: string };
  customer_id?: string;
};

export type TtsPreviewBody = {
  text: string;
  language: 'en' | 'he' | 'yi';
  voice_id?: string;
};

export type FromTtsBody = {
  name: string;
  description?: string;
  folder_id?: string | null;
  language: 'en' | 'he' | 'yi';
  voice_id?: string;
  text: string;
  customer_id?: string;
};
